
import { supabase } from "@/integrations/supabase/client";
import { getTodayUTC } from "../utils/date-utils";
import type { PuzzleData } from '../types/puzzle-types';
import type { Json } from "../types/supabase";

/**
 * Ensures that today's game exists in the database
 * Returns the game ID if successful, null if failed
 */
export async function ensureTodayGameExists(puzzleData: PuzzleData): Promise<string | null> {
  const date = getTodayUTC();
  console.log('Ensuring game exists for date:', date);
  
  try {
    // First check if we already have a game for today
    const { data: existingGame } = await supabase
      .from('games')
      .select('id')
      .eq('date', date)
      .maybeSingle();
      
    if (existingGame?.id) {
      console.log('Game already exists for date:', date, 'with ID:', existingGame.id);
      return existingGame.id;
    }
    
    // Format puzzle data for game creation
    const gameData = {
      date,
      logic_rule: {
        id: puzzleData.correctRule.id,
        name: puzzleData.correctRule.name,
        description: puzzleData.correctRule.description || '',
        reasoning: puzzleData.correctRule.reasoning || ''
      },
      words: puzzleData.words,
      is_closed: false
    };
    
    // Create a new game
    const { data: newGame, error } = await supabase
      .from('games')
      .insert(gameData)
      .select('id')
      .single();
      
    if (error) {
      console.error('Failed to create game in DB:', error);
      // Don't throw here, we can still save puzzle data
    }
    
    // Even if game creation failed, try to ensure we have the puzzle in daily_puzzles table
    await storeDailyPuzzle(date, puzzleData);
    
    return newGame?.id || null;
  } catch (err) {
    console.error('Error ensuring game exists:', err);
    return null;
  }
}

/**
 * Stores the daily puzzle in the database
 */
async function storeDailyPuzzle(date: string, puzzleData: PuzzleData): Promise<void> {
  try {
    // Check if puzzle already exists
    const { data: existingPuzzle } = await supabase
      .from('daily_puzzles')
      .select('id')
      .eq('date', date)
      .maybeSingle();
      
    if (existingPuzzle) {
      console.log('Puzzle already exists for date:', date);
      return;
    }
    
    // Convert PuzzleData to a plain object to make it compatible with Json type
    const puzzleForStorage = JSON.parse(JSON.stringify(puzzleData)) as Json;
    
    // Store the puzzle data
    const { error } = await supabase
      .from('daily_puzzles')
      .insert({
        date,
        puzzle: puzzleForStorage
      });
      
    if (error) {
      console.error('Error storing daily puzzle:', error);
    } else {
      console.log('Successfully stored daily puzzle for date:', date);
    }
  } catch (err) {
    console.error('Error in storeDailyPuzzle:', err);
  }
}

/**
 * Finalizes yesterday's game if needed
 */
export async function finalizeYesterdayGameIfNeeded(): Promise<void> {
  try {
    // Calculate yesterday's date
    const now = new Date();
    now.setDate(now.getDate() - 1);
    const yesterday = now.toISOString().split('T')[0];
    
    // Check if yesterday's game exists and is not closed
    const { data: yesterdayGame } = await supabase
      .from('games')
      .select('id, is_closed')
      .eq('date', yesterday)
      .maybeSingle();
      
    // If no game or already closed, nothing to do
    if (!yesterdayGame || yesterdayGame.is_closed) {
      return;
    }
    
    // Check if we have any rankings in live_stats
    const { data: liveStats } = await supabase
      .from('live_stats')
      .select('word_rankings')
      .eq('game_id', yesterdayGame.id)
      .maybeSingle();
      
    if (liveStats?.word_rankings) {
      // Update the game with the final ranking
      await supabase
        .from('games')
        .update({
          final_ranking: liveStats.word_rankings,
          is_closed: true
        })
        .eq('id', yesterdayGame.id);
        
      console.log('Successfully finalized yesterday\'s game');
    }
  } catch (err) {
    console.error('Error finalizing yesterday\'s game:', err);
  }
}

/**
 * Checks if a user has completed a puzzle for a given date
 */
export async function checkPuzzleCompletion(email: string, date: string): Promise<boolean> {
  if (!email) return false;
  
  try {
    const { data, error } = await supabase
      .from('puzzle_completions')
      .select('*')
      .eq('user_email', email)
      .eq('puzzle_date', date)
      .maybeSingle();
      
    if (error) {
      console.error('Error checking puzzle completion:', error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    console.error('Error in checkPuzzleCompletion:', err);
    return false;
  }
}

/**
 * Records that a user has completed a puzzle for a given date
 */
export async function recordPuzzleCompletion(email: string, date: string): Promise<boolean> {
  if (!email) return false;
  
  try {
    // Check if completion already recorded
    const existing = await checkPuzzleCompletion(email, date);
    if (existing) return true;
    
    // Record completion
    const { error } = await supabase
      .from('puzzle_completions')
      .insert({
        user_email: email,
        puzzle_date: date
      });
      
    if (error) {
      console.error('Error recording puzzle completion:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Error in recordPuzzleCompletion:', err);
    return false;
  }
}
